import React from 'react';
import { ArrowUpRight, HardHat, Zap, Cog, Building2, Wrench, Users } from 'lucide-react';
import { useLang } from '../i18n/LangContext';
import Reveal from './Reveal';
import { IMG } from '../data/realImages';

const ICONS = {
  construction: HardHat,
  electrical: Zap,
  operations: Cog,
  infrastructure: Building2,
  maintenance: Wrench,
  workforce: Users,
};

const IMAGE_OVERLAYS = {
  construction: IMG.construction[0],
  electrical: IMG.electrical[0],
  operations: IMG.industrial[0],
  infrastructure: IMG.industrial[1],
  maintenance: IMG.industrial[3],
  workforce: IMG.workforce[0],
};

export default function Divisions() {
  const { t } = useLang();
  const items = t('divisions.items');

  return (
    <section id="divisions" data-testid="divisions-section" className="relative py-24 md:py-40 border-t border-[#27272A]">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <Reveal className="section-index">{t('divisions.index')}</Reveal>
            <Reveal as="h2" className="h-section text-[#F5F5F2] mt-4 max-w-3xl reveal-delay-1">
              {t('divisions.title')}
            </Reveal>
          </div>
          <Reveal className="text-[#A1A1AA] max-w-md text-sm md:text-base leading-relaxed reveal-delay-2">
            {t('divisions.sub')}
          </Reveal>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5">
          {items.map((item, idx) => {
            const Icon = ICONS[item.id] || Cog;
            // Bento sizes
            const span = idx === 0 ? 'md:col-span-8' : idx === 5 ? 'md:col-span-8' : 'md:col-span-4';
            const isFeatured = idx === 0 || idx === 5;
            return (
              <Reveal
                key={item.id}
                className={`${span} reveal-delay-${(idx % 4) + 1}`}
              >
                <div
                  data-testid={`division-card-${item.id}`}
                  className={`bento-card relative h-full p-7 md:p-9 flex flex-col justify-between min-h-[260px] ${isFeatured ? 'md:min-h-[340px]' : ''} overflow-hidden`}
                >
                  {isFeatured && (
                    <div className="absolute inset-0 -z-0 opacity-25 group-hover:opacity-40 transition-opacity">
                      <img src={IMAGE_OVERLAYS[item.id]} alt="" className="w-full h-full object-cover" loading="lazy" />
                      <div className="absolute inset-0 bg-gradient-to-tr from-[#0A0A0B] via-[#0A0A0B]/85 to-transparent" />
                    </div>
                  )}
                  <div className="relative z-10 flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-[10px] tracking-[0.3em] text-[#C8102E]">0{idx + 1}</span>
                      <Icon size={18} className="text-[#A1A1AA]" />
                    </div>
                    <ArrowUpRight size={20} className="arrow-icon text-[#A1A1AA]" />
                  </div>
                  <div className="relative z-10 mt-12">
                    <h3 className={`font-display ${isFeatured ? 'text-3xl md:text-4xl' : 'text-2xl'} tracking-tight text-[#F5F5F2]`}>
                      {item.title}
                    </h3>
                    <p className="mt-4 text-sm md:text-base text-[#A1A1AA] leading-relaxed max-w-md">
                      {item.desc}
                    </p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
